import { useState, useCallback, useEffect } from "react";
import type { GetSurveyResultsResponse } from "@event-planner/shared";
import {
  createSurvey,
  closeSurvey,
  reopenSurvey,
  deleteSurvey,
  getSurveyResults,
  type CreateSurveyInput,
} from "../services/surveyService";

export function useCreateSurvey() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(async (input: CreateSurveyInput): Promise<{ surveyId: string; notificationId: string | null }> => {
    setLoading(true);
    setError(null);
    try {
      return await createSurvey(input);
    } catch (err) {
      const e =
        err instanceof Error ? err : new Error("Create survey failed");
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { create, loading, error };
}

export function useCloseSurvey() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const close = useCallback(async (surveyId: string) => {
    setLoading(true);
    setError(null);
    try {
      await closeSurvey(surveyId);
    } catch (err) {
      const e =
        err instanceof Error ? err : new Error("Close survey failed");
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { close, loading, error };
}

export function useReopenSurvey() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const reopen = useCallback(async (surveyId: string) => {
    setLoading(true);
    setError(null);
    try {
      await reopenSurvey(surveyId);
    } catch (err) {
      const e =
        err instanceof Error ? err : new Error("Reopen survey failed");
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { reopen, loading, error };
}

/**
 * Fetches server-computed aggregate results for a survey. Results are
 * anonymous aggregates only — never individual response docs.
 *
 * Accepts an optional `refreshToken` so callers can force a re-fetch
 * after a mutation (e.g. after closing the survey when more responses
 * may have landed).
 */
export function useSurveyResults(
  surveyId: string | undefined,
  refreshToken?: unknown,
) {
  const [results, setResults] = useState<GetSurveyResultsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!surveyId) {
      setResults(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSurveyResults(surveyId)
      .then((r) => {
        if (!cancelled) setResults(r);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error("Failed to load results"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [surveyId, refreshToken]);

  return { results, loading, error };
}

export function useDeleteSurvey() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(async (surveyId: string) => {
    setLoading(true);
    setError(null);
    try {
      await deleteSurvey(surveyId);
    } catch (err) {
      const e =
        err instanceof Error ? err : new Error("Delete survey failed");
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { remove, loading, error };
}
